import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import RouteLoadingShell from "@/components/shared/RouteLoadingShell";
import { listMyAccounts, setCurrentAccountId } from "@/lib/api";
import { resolveRelinkAccountId } from "@/lib/account-linking";
import { redirectToAuth, redirectToAuthIfRequired } from "@/lib/auth-required-redirect";
import {
  resolveAuthenticatedSupabaseUser,
} from "@/lib/onboarding-auth";
import { getSupabaseBrowserClient } from "@/lib/supabase-browser-client";
import SupportRelink, { readSupportRelinkToken } from "@/pages/SupportRelink";

function readRequestedAccountId(search = "") {
  return new URLSearchParams(String(search || "").replace(/^\?/, "")).get("accountId") || "";
}

export default function Relink() {
  const location = useLocation();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const supportRelinkToken = readSupportRelinkToken(location.search);

  useEffect(() => {
    if (supportRelinkToken) {
      return undefined;
    }
    let cancelled = false;

    async function bootstrap() {
      try {
        setErrorMessage("");
        const client = getSupabaseBrowserClient();
        if (!client) {
          redirectToAuth(navigate, { location });
          return;
        }
        const {
          data: { session },
        } = await client.auth.getSession();
        const user = await resolveAuthenticatedSupabaseUser(client, session);
        if (!user) {
          redirectToAuth(navigate, { location });
          return;
        }

        const accountsPayload = await listMyAccounts({ forceFresh: true });
        if (cancelled) {
          return;
        }
        const accountId = resolveRelinkAccountId(accountsPayload, {
          requestedAccountId: readRequestedAccountId(location.search),
        });

        if (!accountId) {
          navigate("/link", { replace: true });
          return;
        }

        setCurrentAccountId(accountId);
        navigate(`/link?relink=1&accountId=${encodeURIComponent(accountId)}`, { replace: true });
      } catch (error) {
        if (cancelled) {
          return;
        }
        const message = error instanceof Error ? error.message : "Could not open relink right now.";
        if (/no linked account found/i.test(message)) {
          navigate("/link", { replace: true });
          return;
        }
        if (redirectToAuthIfRequired(error, { navigate, location })) {
          return;
        }
        setErrorMessage(message);
      }
    }

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, navigate, supportRelinkToken]);

  if (supportRelinkToken) {
    return <SupportRelink token={supportRelinkToken} />;
  }

  if (errorMessage) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center px-5 text-center text-sm text-destructive">
        {errorMessage}
      </div>
    );
  }

  return (
    <RouteLoadingShell
      title="Opening relink…"
      copy="Checking your signed-in Min account and taking you to the right WhatsApp relink flow."
    />
  );
}
